// apps/web/src/components/miner/EpochDrillDown.tsx
import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { formatGNK } from './minerFormat.js';
import type { MinerRewardNodesResponse } from '../../lib/minerApi.js';

type State =
  | { status: 'loading' }
  | { status: 'error'; onRetry?: () => void }
  | { status: 'success'; data: MinerRewardNodesResponse };

interface Props {
  readonly state: State;
}

export function EpochDrillDown({ state }: Props): ReactNode {
  const { t } = useTranslation();

  if (state.status === 'loading') {
    return (
      <div className="px-3.5 pt-1.5 pb-3.5">
        <div className="rounded-lg bg-surface-2 p-3 space-y-2">
          {[0, 1, 2].map((i) => (
            <div key={i} className="h-3 rounded bg-surface-3 opacity-40 animate-pulse" />
          ))}
        </div>
      </div>
    );
  }

  if (state.status === 'error') {
    return (
      <div className="px-3.5 pt-1.5 pb-3.5">
        <button
          type="button"
          onClick={state.onRetry}
          className="w-full rounded-lg bg-surface-2 px-3 py-3 text-left font-mono text-xs text-text-tertiary hover:text-text-secondary"
        >
          {t('miner.drillError')}
        </button>
      </div>
    );
  }

  const data = state.data;
  const showMeta = data.epoch_start || data.epoch_end;
  const firstNode = data.nodes.length > 0 ? data.nodes[0] : undefined;

  return (
    <div className="px-3.5 pt-1.5 pb-3.5">
      {showMeta && (
        <div className="flex justify-between px-0.5 pt-2 pb-2.5 font-mono text-[10px] uppercase tracking-wider text-text-tertiary">
          {data.epoch_start && (
            <span>{t('miner.drillStarted', { date: formatDateUTC(data.epoch_start) })}</span>
          )}
          {data.epoch_end && (
            <span>{t('miner.drillEnded', { date: formatDateUTC(data.epoch_end) })}</span>
          )}
        </div>
      )}

      {!data.has_breakdown ? (
        <div className="rounded-lg bg-surface-2 px-3 py-3 font-mono text-xs text-text-tertiary">
          {t('miner.drillNoBreakdown', { count: data.nodes_count })}
        </div>
      ) : data.nodes_count === 1 && firstNode ? (
        <SingleNodeRow node={firstNode} />
      ) : (
        <NodesTable nodes={data.nodes} />
      )}
    </div>
  );
}

function SingleNodeRow({ node }: { node: MinerRewardNodesResponse['nodes'][number] }): ReactNode {
  const { t } = useTranslation();
  return (
    <div className="rounded-lg bg-surface-2 px-3 py-2.5 font-mono text-xs text-text-secondary">
      {t('miner.drillSingleNode', {
        weight: node.poc_weight.toLocaleString(),
        reward: formatGNK(parseFloat(node.reward_gnk)),
      })}
    </div>
  );
}

function NodesTable({ nodes }: { nodes: MinerRewardNodesResponse['nodes'] }): ReactNode {
  const { t } = useTranslation();
  return (
    <div className="overflow-x-auto rounded-lg bg-surface-2 max-h-[280px] overflow-y-auto">
      <div className="grid min-w-[500px] grid-cols-[minmax(60px,1fr)_56px_72px_96px_88px_44px] gap-2 bg-surface-3 px-3 py-2.5 text-[9px] font-semibold uppercase tracking-[1.1px] text-text-tertiary sticky top-0">
        <span>{t('miner.drillNode')}</span>
        <span className="text-right">{t('miner.drillWeight')}</span>
        <span className="text-right">{t('miner.model')}</span>
        <span className="text-right">{t('miner.scaledWeight')}</span>
        <span className="text-right">{t('miner.drillReward')}</span>
        <span className="text-right">{t('miner.drillShare')}</span>
      </div>
      {nodes.map((n) => (
        <div
          key={n.local_id}
          className="grid min-w-[500px] grid-cols-[minmax(60px,1fr)_56px_72px_96px_88px_44px] gap-2 border-t border-border-hairline px-3 py-2.5 font-mono text-xs"
        >
          <span className="text-link">{n.local_id}</span>
          <span className="text-right text-text-secondary">{n.poc_weight.toLocaleString()}</span>
          <span className="block overflow-hidden truncate text-right text-text-secondary">{n.model_name ?? '—'}</span>
          <span className="text-right text-text-secondary">
            {n.scaled_weight != null ? n.scaled_weight.toLocaleString() : '—'}
          </span>
          <span className="text-right font-medium text-text-primary">{formatGNK(parseFloat(n.reward_gnk))}</span>
          <span className="text-right text-[11px] text-text-tertiary">{n.weight_percent}%</span>
        </div>
      ))}
    </div>
  );
}

function formatDateUTC(iso: string): string {
  const d = new Date(iso);
  if (isNaN(d.getTime())) return iso;
  const day = String(d.getUTCDate()).padStart(2, '0');
  const month = d.toLocaleString('en-US', { month: 'short', timeZone: 'UTC' });
  const hh = String(d.getUTCHours()).padStart(2, '0');
  const mm = String(d.getUTCMinutes()).padStart(2, '0');
  return `${day} ${month} · ${hh}:${mm}`;
}
